import { ExportDto, RequestCompression, exportFile } from '@ecosystem/export-api';
import { ExportFormat, ExportModal, authentication, downloadFile, logger } from '@ecosystem/newron-client-lib';
import { actions, elements, overlays } from '@ecosystem/newron-design-system';
import { Mode } from '@ecosystem/newron-design-system/build/components/buttons/Button';
import React, { useEffect, useState } from 'react';

import { trackDataExportInitiated, trackDataExported } from '../analytics/trackDataExported';
import { useDataState } from '../data/provider';
import { DataStore } from '../data/store';
import { ComponentHeaderActionType } from '../page/types';
import { hasScrollbarRecursive } from '../tools/hasScrollbar';
import { BasicTrackingFn, Params, SettingsProvider } from '../types';

import { roundExportValues } from './utils/roundExportValue';

const { Button, Menu } = actions;
const { Icon } = elements;
const { Toast } = overlays;

const exportModalMessage = "You're about to download the current data set.";

type ExportButtonProps = {
  trackingSourceData: DataStore;
  disabled?: boolean;
  exportFormats: ExportFormat[];
  getExportData: (exportFormat: ExportFormat, data?: DataStore) => Promise<ExportDto>;
  exportContentRef?: React.MutableRefObject<HTMLElement[] | HTMLElement>;
  onModalVisibilityChange?: (isVisible: boolean) => void;
  componentSettings: Params;
  componentMetadata?: SettingsProvider['metadata'];
  trackEvent?: BasicTrackingFn;
  hasExternalExportContent?: boolean;
  buttonType?: Mode;
  actionType?: ComponentHeaderActionType;
};

function ExportButton({
  trackingSourceData,
  componentSettings,
  disabled,
  exportFormats,
  getExportData,
  exportContentRef,
  onModalVisibilityChange,
  componentMetadata,
  trackEvent,
  hasExternalExportContent = false,
  buttonType,
  actionType = ComponentHeaderActionType.Button,
}: Readonly<ExportButtonProps>) {
  const [showExportModal, setShowExport] = useState(false);
  const [message, setMessage] = useState<string>(exportModalMessage);
  const [disabledFormats, setDisabledFormats] = useState<ExportFormat[]>([]);
  const [toast, setToast] = useState<{ type: overlays.Notification; message: string }>();
  const { data } = useDataState();
  useEffect(() => {
    if (onModalVisibilityChange) {
      onModalVisibilityChange(showExportModal);
    }
  }, [showExportModal, onModalVisibilityChange]);

  const handleExportButtonClick = () => {
    if (exportFormats.some(format => format === ExportFormat.Pptx) && exportContentRef?.current) {
      try {
        const elementRefs = Array.isArray(exportContentRef.current)
          ? exportContentRef.current
          : [exportContentRef.current];
        const hasScrollbar = elementRefs.filter(Boolean).some(hasScrollbarRecursive);
        if (hasScrollbar && !hasExternalExportContent) {
          setDisabledFormats([ExportFormat.Pptx]);
          setMessage(`${exportModalMessage} Powerpoint export is temporarily unavailable.`);
          logger.error(new Error('Powerpoint export not available. Scrollbar in the export image.'), {
            screenHeight: window.screen.height,
            screenWidth: window.screen.width,
          });
        } else if (disabledFormats.length > 0) {
          setDisabledFormats(disabledFormats.filter(format => format !== ExportFormat.Pptx));
          setMessage(exportModalMessage);
        }
      } catch (err) {
        logger.error(err);
      }
    }
    trackEvent && trackDataExportInitiated(trackEvent, componentSettings, componentMetadata);
    setShowExport(true);
  };

  const handleDownload = async (selectedExportFormats: ExportFormat[]) => {
    const token = await authentication.getAccessToken();
    const optionChosen: string[] = [];
    for (const exportFormat of selectedExportFormats) {
      const exportData = await getExportData(exportFormat, trackingSourceData ?? data);
      roundExportValues(exportData);
      optionChosen.push(exportData.template);
      const file = await exportFile(exportData, {
        baseUrl: '/export',
        bearerToken: token,
        requestCompression: RequestCompression.GZip,
      });
      downloadFile(file);
    }
    trackEvent &&
      trackDataExported(trackEvent, componentSettings, componentMetadata, optionChosen, trackingSourceData ?? data);
  };

  return (
    <>
      {actionType === ComponentHeaderActionType.Button ? (
        <Button
          testId="export-data-set-button"
          onClick={handleExportButtonClick}
          mode={buttonType ? buttonType : `secondary`}
          disabled={disabled}
          label="Export data set"
        >
          <Button.Icon>
            <Button.Icon.icons.Export />
          </Button.Icon>
        </Button>
      ) : (
        <Menu.Item onSelect={handleExportButtonClick} testId="export-data-set-menu-item">
          <Icon>
            <Icon.icons.Export />
          </Icon>
          Export
        </Menu.Item>
      )}
      <ExportModal
        isShown={showExportModal}
        title="Download data"
        message={message}
        exportFormats={exportFormats}
        disabledFormats={disabledFormats}
        download={handleDownload}
        onError={err => {
          setToast({ type: 'error', message: 'Something went wrong' });
          setShowExport(false);
          logger.error(err);
        }}
        onClose={() => setShowExport(false)}
      />

      {toast && (
        <Toast type={toast.type} onComplete={() => setToast(undefined)}>
          {toast.message}
        </Toast>
      )}
    </>
  );
}

export { ExportButton, ExportButtonProps };
